/**
 * DMCA & Copyright Policy Page
 *
 * Place at: src/pages/DMCAPage.jsx
 * Layout:   src/components/legal/LegalLayout.jsx
 *
 * Section numbers are stable anchors (/dmca#4.4 and /dmca#4.5 are linked
 * from the Terms). Never renumber.
 *
 * ── Version history (newest first) ─────────────────────────────────────
 *
 * October 2026 (alongside TOS v5)
 * - §4.1: designated agent block filled from the U.S. Copyright Office
 *   registration (AGENT constant below). Pending note removed.
 * - Part 1 (old §3.x Acceptable Use) replaced by a pointer to the new
 *   standalone Acceptable Use Policy at /acceptable-use, so there is one
 *   AUP, not two that can drift apart. §3.1–3.3 anchors are kept so old
 *   links still land somewhere useful.
 * - §4.2: notices go to the designated agent; full 17 U.S.C. §512(c)(3)
 *   elements listed.
 * - §4.4: counter-notice now includes consent to federal court
 *   jurisdiction (§512(g)(3)(D)) — required, and missing before.
 * - Layout moved to shared LegalLayout.jsx.
 *
 * July 23, 2026 — legal audit fixes
 * - §4.3: "within 24 hours" replaced with the statutory "expeditiously".
 * - §4.1: prepared for agent registration.
 *
 * ── Deploy checklist ───────────────────────────────────────────────────
 * Fill in AGENT below EXACTLY as filed at dmca.copyright.gov. If the page
 * and the directory listing differ, the safe harbor is weaker.
 * Registration expires 3 years after filing — renew before REGISTERED_ON
 * + 3 years or the §512 safe harbor lapses.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Scale } from 'lucide-react';
import {
  PageShell, Section, BulletList, LINK,
} from '../components/legal/LegalLayout';

const EFFECTIVE_DATE = 'October 18, 2026'; // TODO: set to deploy date

/**
 * Designated DMCA agent — must match the Copyright Office directory
 * listing word for word.
 * TODO: fill in from your registration confirmation.
 */
const AGENT = {
  name: '',           // agent name or title, as registered
  organization: '',   // service provider name, as registered
  mailingAddress: '', // as registered (PO box is fine if that's what you filed)
  phone: '',          // as registered
  email: 'support@enume.io',
  registeredOn: '',   // e.g. 'October 17, 2026' — renew within 3 years
};

const agentComplete = Boolean(AGENT.name && AGENT.mailingAddress && AGENT.phone);

if (import.meta.env.DEV && !agentComplete) {
  // eslint-disable-next-line no-console
  console.warn('[DMCAPage] AGENT is incomplete — §4.1 must match the Copyright Office registration.');
}

const Mail = () => <a href={`mailto:${AGENT.email}`} className={LINK}>{AGENT.email}</a>;

/* ── Page ──────────────────────────────────────────────────────────── */

const DMCAPage = () => (
  <PageShell current="/dmca">
    <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 tracking-tight">
      Copyright & DMCA Policy
    </h1>
    <p className="text-zinc-500 text-sm mb-10">Effective {EFFECTIVE_DATE}</p>

    {/* ══ PART 1: ACCEPTABLE USE (moved) ═══════════════════════════ */}
    <div className="mb-10">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="w-5 h-5 text-amber-400" />
        <h2 className="text-xl font-bold text-white">Acceptable Use</h2>
      </div>
      {/* Anchors kept for old /dmca#3.x links. */}
      <span id="3.1" className="block scroll-mt-20" />
      <span id="3.2" className="block scroll-mt-20" />
      <span id="3.3" className="block scroll-mt-20" />
      <p className="text-sm text-zinc-400">
        Rules about what you may post and how you may use Enume now live in our{' '}
        <Link to="/acceptable-use" className={LINK}>Acceptable Use Policy</Link>, which is part of
        the <Link to="/terms" className={LINK}>Terms of Service</Link>. This page covers copyright
        and the DMCA.
      </p>
    </div>

    {/* ══ PART 2: DMCA POLICY ═════════════════════════════════════ */}
    <div className="pt-10 border-t border-zinc-800/50">
      <div className="flex items-center gap-2 mb-6">
        <Scale className="w-5 h-5 text-indigo-400" />
        <h2 className="text-xl font-bold text-white">DMCA & Copyright Policy</h2>
      </div>
      <p className="text-sm text-zinc-400 mb-6">
        Enume respects intellectual property rights and responds to notices of alleged
        infringement under the Digital Millennium Copyright Act, 17 U.S.C. § 512.
      </p>

      <div className="space-y-8">
        <Section id="4.1" title="Designated DMCA Agent">
          <p>Notices of claimed infringement and counter-notifications must be sent to Enume's designated agent:</p>
          <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700/50 space-y-1 text-zinc-300">
            {AGENT.name && <p><span className="text-zinc-500">Agent:</span> {AGENT.name}</p>}
            {AGENT.organization && <p><span className="text-zinc-500">Service provider:</span> {AGENT.organization}</p>}
            {AGENT.mailingAddress && <p><span className="text-zinc-500">Mailing address:</span> {AGENT.mailingAddress}</p>}
            {AGENT.phone && <p><span className="text-zinc-500">Telephone:</span> {AGENT.phone}</p>}
            <p><span className="text-zinc-500">Email:</span> <Mail /></p>
          </div>
          <p className="text-zinc-500 text-xs">
            Our designated agent is registered with the{' '}
            <a href="https://dmca.copyright.gov/osp/" target="_blank" rel="noopener noreferrer" className={LINK}>
              U.S. Copyright Office DMCA Designated Agent Directory
            </a>. Only copyright notices go to this address; other questions should go to support@enume.io.
          </p>
        </Section>

        <Section id="4.2" title="Filing a Takedown Notice">
          <p>To report infringement, send the designated agent a written notice that includes:</p>
          <BulletList items={[
            'A physical or electronic signature of the copyright owner or a person authorized to act on their behalf',
            'Identification of the copyrighted work claimed to be infringed (or, for multiple works, a representative list)',
            'Identification of the material claimed to be infringing and information reasonably sufficient for us to locate it, such as its URL on Enume',
            'Your name, mailing address, telephone number, and email address',
            'A statement that you have a good-faith belief that the use is not authorized by the copyright owner, its agent, or the law',
            'A statement that the information in the notice is accurate, and, under penalty of perjury, that you are the owner or authorized to act on the owner\'s behalf',
          ]} />
          <p>Notices missing these elements may not be acted on. Before filing, consider whether the use is licensed — everything posted publicly on Enume is licensed under CC BY-SA 4.0, and uses that follow that license (including attribution) are authorized.</p>
        </Section>

        <Section id="4.3" title="Processing">
          <p>Upon receiving a valid notice:</p>
          <BulletList items={[
            'We remove or disable access to the material expeditiously, typically within two business days',
            'We notify the uploader and provide a copy of the notice, which may include the complainant\'s contact information',
            'We record a copyright strike on the uploader\'s account (Section 4.5)',
            'We keep a record of the notice for our records and transparency reporting',
          ]} />
        </Section>

        <Section id="4.4" title="Counter-Notification">
          <p>If you believe your material was removed by mistake or misidentification, send the designated agent a counter-notification that includes:</p>
          <BulletList items={[
            'Your physical or electronic signature',
            'Identification of the material that was removed and where it appeared before removal',
            'A statement under penalty of perjury that you have a good-faith belief the material was removed as a result of mistake or misidentification',
            'Your name, address, and telephone number',
            'A statement that you consent to the jurisdiction of the federal district court for the judicial district where your address is located (or, if your address is outside the United States, any judicial district in which Enume may be found), and that you will accept service of process from the person who filed the original notice or their agent',
          ]} />
          <p>We forward valid counter-notifications to the original complainant. Unless they notify us within 10 business days that they have filed a court action seeking to restrain the activity, we restore the material within 10 to 14 business days after receiving the counter-notification and remove the associated strike.</p>
        </Section>

        <Section id="4.5" title="Repeat Infringer Policy">
          <p>Enume terminates, in appropriate circumstances, the accounts of users who are repeat infringers.</p>
          <BulletList items={[
            '1 strike: warning and content removal',
            '2 strikes: 30-day account suspension and content removal',
            '3 strikes: permanent account termination',
          ]} />
          <p>A copyright strike expires 12 months after it was issued if no new strikes are received in that time. Strikes removed through a successful counter-notification do not count. Copyright strikes are tracked separately from moderation strikes under the <Link to="/terms#1.7" className={LINK}>Terms</Link>.</p>
        </Section>

        <Section id="4.6" title="Misuse of the DMCA Process">
          <p>Under 17 U.S.C. § 512(f), anyone who knowingly materially misrepresents that material is infringing, or that it was removed by mistake, may be liable for damages. We may disregard notices we believe are fraudulent or abusive.</p>
        </Section>
      </div>
    </div>

    <p className="text-zinc-500 text-xs pt-8">
      Copyright notices: <Mail /> · Other questions: <a href="mailto:support@enume.io" className={LINK}>support@enume.io</a>
    </p>
  </PageShell>
);

export default DMCAPage;